import type React from "react";
import {useNavigate} from "react-router";
import {
    ChevronRightOutlined,
    DescriptionOutlined,
    EuroOutlined,
    GavelOutlined,
    PaidOutlined,
    ReceiptLongOutlined,
    SavingsOutlined,
    TimelineOutlined,
} from "@mui/icons-material";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2TabKey} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {BASELINE_DAYS} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {
    formatApprox,
    formatEur,
    formatInt,
    formatRatio,
    PT_V2_COLORS,
    termDays,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {
    usePtV2DistributionQuery,
    usePtV2ProfileQuery,
} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2Bars} from "~/experiments/pages/payment-terms-v2/components/PtV2Bars.tsx";
import {PtV2FastSpendCard} from "~/experiments/pages/payment-terms-v2/components/PtV2FastSpendCard.tsx";
import {usePtV2Period} from "~/experiments/pages/payment-terms-v2/usePtV2Period.ts";

type Opportunity = {tab: PtV2TabKey; icon: React.ReactNode; title: string; value: string; sub: string; text: string; go: string};

export const OverviewTab: React.FC = () => {
    const {summary, hasData, tabPath} = usePtV2Context();
    const navigate = useNavigate();
    const distQ = usePtV2DistributionQuery(hasData);
    const profileQ = usePtV2ProfileQuery(hasData);
    const k = summary?.kpis;
    const profile = profileQ.data;
    const dist = distQ.data ?? [];
    const period = usePtV2Period();

    const contractClauses = profile?.termTypes.reduce((s, t) => s + (t.clauses ?? 0), 0) ?? null;
    const kpis: SitKpiPill[] = [
        {tone: 'plain', icon: <EuroOutlined/>, label: 'Total spend (last 12 months)', value: formatEur(k?.totalSpendL12m),
            help: 'Spend in this databag over the 12 months up to its latest transaction.'},
        {tone: 'ok', icon: <SavingsOutlined/>, label: 'Working capital identified',
            value: formatEur(k ? k.leakageWc + k.harmonizationWc : null),
            help: 'Payment term leakage plus term harmonization at the Net 60 baseline.'},
        {tone: 'info', icon: <ReceiptLongOutlined/>, label: 'ERP invoices', value: formatInt(k?.softcoInvoices)},
        {tone: 'plain', icon: <GavelOutlined/>, label: 'Contract terms', value: formatInt(contractClauses)},
        {tone: 'plain', icon: <DescriptionOutlined/>, label: 'PDF invoice headers', value: formatInt(profile?.pdfCoverage?.total)},
    ];

    const opportunities: Opportunity[] = [
        {
            tab: 'leakage', icon: <GavelOutlined/>, title: 'Payment term leakage', value: formatEur(k?.leakageWc),
            sub: `across ${formatInt(k?.leakageSuppliers)} suppliers`,
            text: 'The ERP pays these suppliers sooner than the contract requires. Fixing the vendor master releases the cash at no negotiation cost.',
            go: 'Review suppliers',
        },
        {
            tab: 'harmonization', icon: <TimelineOutlined/>, title: 'Term harmonization', value: formatEur(k?.harmonizationWc),
            sub: `across ${formatInt(k?.harmonizationSuppliers)} suppliers`,
            text: 'Moving the largest suppliers paid faster than Net 60 to the baseline. Needs renegotiation with each supplier.',
            go: 'Model target terms',
        },
        {
            tab: 'discounts', icon: <PaidOutlined/>, title: 'Discount capture',
            value: formatInt(k ? k.invoiceDiscountOffers + k.contractDiscountOffers : null),
            sub: `${formatInt(k?.invoiceDiscountOffers)} invoice terms, ${formatInt(k?.contractDiscountOffers)} contracts`,
            text: 'Cash discounts that are paid on standard terms today. Check the annualised yield before routing them to a fast payment run.',
            go: 'Check the yield',
        },
    ];

    const erpTotal = dist.reduce((s, r) => s + r.invoiceCount, 0);
    const erpRows = dist.map(r => {
        const d = termDays(r.termCode);
        return {
            key: r.termCode || 'not-set',
            label: r.termCode || 'Not set',
            sub: r.termCode ? r.label : undefined,
            value: r.invoiceCount,
            color: d == null ? PT_V2_COLORS.notSet : d >= BASELINE_DAYS ? PT_V2_COLORS.atBaseline : PT_V2_COLORS.belowBaseline,
            display: <>{formatInt(r.invoiceCount)}<span>{formatRatio(erpTotal ? r.invoiceCount / erpTotal : null)}</span></>,
        };
    });

    return <PtV2TabFrame
        tab="overview"
        kpis={kpis}
        kpiAside={period}
        loading={distQ.isPending || profileQ.isPending}
        error={distQ.isError || profileQ.isError}>
        <div className="ptv2-section">
            <div className="ptv2-grid3">
                {opportunities.map(o => <button key={o.tab} type="button" className="ptv2-opp" onClick={() => navigate(tabPath(o.tab))}>
                    <span className="ptv2-opp-top"><span className="ptv2-opp-ico">{o.icon}</span>{o.title}</span>
                    <span className="ptv2-opp-val ptv2-num">{o.value}<small>{o.sub}</small></span>
                    <span className="ptv2-opp-txt">{o.text}</span>
                    <span className="ptv2-opp-go">{o.go}<ChevronRightOutlined fontSize="small"/></span>
                </button>)}
            </div>
            <div className="ptv2-grid2">
                <PtV2Card title="Terms applied on ERP invoices">
                    <PtV2Bars rows={erpRows}/>
                    <div className="ptv2-legend">
                        <span><i style={{background: PT_V2_COLORS.atBaseline}}/>At or above Net 60</span>
                        <span><i style={{background: PT_V2_COLORS.belowBaseline}}/>Shorter than Net 60</span>
                        <span><i style={{background: PT_V2_COLORS.notSet}}/>No term on invoice</span>
                    </div>
                </PtV2Card>
                <PtV2FastSpendCard rows={dist} totalSpend={k?.fasterThanBaselineSpend} totalInvoices={k?.fasterThanBaselineInvoices}/>
                <PtV2Card title="Payment term types in contracts">
                    <PtV2Bars rows={(profile?.termTypes ?? []).map(t => ({
                        key: t.label, label: t.label, value: t.clauses ?? 0,
                        display: <>{formatApprox(t.clauses, t.approximate)}<span>{t.contracts != null ? `${formatInt(t.contracts)} ctr` : ''}</span></>,
                    }))}/>
                    <p className="ptv2-foot">
                        Clauses, with the number of distinct contracts where known. {formatInt(contractClauses)} clauses in total.
                    </p>
                </PtV2Card>
                <PtV2Card title="Net days agreed in contracts">
                    <PtV2Bars rows={(profile?.netDays ?? []).map(t => ({
                        key: t.label, label: t.label, sub: t.note ?? undefined, value: t.contracts ?? 0,
                        color: termDays(t.label.replace(/^Net\s*/i, 'NT')) === BASELINE_DAYS ? PT_V2_COLORS.atBaseline : undefined,
                        display: <>{formatApprox(t.contracts, t.approximate)}<span>contracts</span></>,
                    }))}/>
                </PtV2Card>
            </div>
        </div>
    </PtV2TabFrame>;
};
